import { useCallback, useMemo } from 'react';
import { typeCatalog } from 'variant';
import { useProjectModel } from '../../model.js';
import { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';

const _DevCenterComponentTypes = typeCatalog(DevCenterComponent);
type ExtensionType = keyof typeof _DevCenterComponentTypes;
type ExtensionsMap = { [K in ExtensionType]: Array<DevCenterComponent<K>> };

export type DashboardExtension =
  | 'DashboardPage'
  | 'DashboardMenuPlugin'
  | 'DashboardPlugin'
  | 'DashboardModal';

export function useExtensions() {
  const { model } = useProjectModel();
  const extensionsByType = useMemo(() => {
    const extensionsMap = model.components.reduce<ExtensionsMap>(
      (acc, component) => {
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        acc[component.type].push(component as any);
        return acc;
      },
      {
        DashboardPage: [],
        DashboardSidebarCategory: [],
        EmbeddedScript: [],
        CustomElement: [],
        BlocksWidget: [],
        BlocksMainComponent: [],
        BlocksWidgetPanel: [],
        DashboardPlugin: [],
        DashboardMenuPlugin: [],
        DashboardModal: [],
        Topology: [],
        Api: [],
        WebMethod: [],
        Event: [],
        ServicePlugin: [],
        SitePlugin: [],
        Monitoring: [],
        SiteComponent: [],
        SiteComponentPanel: [],
      }
    );
    extensionsMap.DashboardPage = extensionsMap.DashboardPage.sort((a, b) =>
      a.route.localeCompare(b.route)
    );
    return extensionsMap;
  }, [model.components]);

  const dashboardExtensions = useMemo(
    () => [
      ...extensionsByType.DashboardPage,
      ...extensionsByType.DashboardMenuPlugin,
      ...extensionsByType.DashboardPlugin,
      ...extensionsByType.DashboardModal,
    ],
    [extensionsByType]
  );

  const hasExtensions = useCallback(
    (extensionType?: ExtensionType) =>
      extensionType
        ? extensionsByType[extensionType].length > 0
        : model.components.length > 0,
    [extensionsByType, model.components]
  );

  const getExtensions = useCallback(
    <T extends ExtensionType>(extensionType: T) =>
      extensionsByType[extensionType],
    [extensionsByType]
  );

  return {
    dashboardExtensions,
    getExtensions,
    hasExtensions,
  };
}
